import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Menu,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGrid } from "@mui/x-data-grid";
import SearchBar, { useDebounce } from "../../components/SearchBar";
import { CustomNoRowsOverlay } from "../../components/common/OverlayGrid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { CiFilter } from "react-icons/ci";
import { MdOutlineFileDownload, MdOutlineVisibility } from "react-icons/md";
import { RiDeleteBin5Line, RiEditLine } from "react-icons/ri";
import { Offcanvas } from "react-bootstrap";
import { FilterCertificate } from "./FilterCertificate";
import { useSelector } from "react-redux";
import { useTheme } from "../../ThemeContext";
import { AddCertificate } from "./AddCertificate";
import {
  useFetchGetallDepartmentsQuery,
  useFetchCertificatesQuery,
  useDeleteAwardMutation,
  useLazyDownloadCertificateQuery,
} from "../../slice/logdetailSlice";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { fetchAllEntities } from "../../apis/apiService";
import Loader from "../../components/common/Loader";
import "../../App.css";
import { openInNewTab } from "../../utils/openInNewTab";


const Certificate = () => {
  const { isDarkMode } = useTheme();

  // Role-based access (Define this FIRST before using in other hooks)
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  const { role: login_user_role } = login_user || {};
  const maxSelectableYear = dayjs().add(1, "year").endOf("year");
  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [quarter, setQuarter] = useState("all");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [certificates] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuAnchorPos, setMenuAnchorPos] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    employee_id: [],
    department_id: [],
    period_id: [],
    award_id: [],
  });

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const [quarterOptions, setQuarterOptions] = useState([{ label: "All Quarters", value: "all" }]);
  const periodsFetched = useRef(false);

  useEffect(() => {
    if (periodsFetched.current) return;
    periodsFetched.current = true;

    const fetchAwardPeriods = async () => {
      try {
        const response = await fetchAllEntities("master/award-periods/show");
        if (response && response.data) {
          const periodsArray = Array.isArray(response.data)
            ? response.data
            : Object.values(response.data);

          if (periodsArray.length > 0) {
            const formattedOptions = [
              { label: "All Quarters", value: "all" },
              ...periodsArray.map((q) => ({
                label: q.period_label || q.label,
                value: String(q.id || q.period_id || q.value),
                code: q.period_code || q.code,
                period_label: q.period_label || q.label,
              })).sort((a, b) => a.label.localeCompare(b.label)),
            ];
            setQuarterOptions(formattedOptions);
          }
        }
      } 
      catch (err) {
        console.error("Error fetching award periods:", err);
      }
    };
    fetchAwardPeriods();
  }, []);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { data: rawCertificates, isLoading, isFetching, refetch } = useFetchCertificatesQuery({
    year: selectedYear ? dayjs(selectedYear).year().toString() : "",
    period_id: Array.isArray(advancedFilters.period_id) && advancedFilters.period_id.length > 0
      ? advancedFilters.period_id.join(",")
      : (quarter === "all" ? "" : quarter),
    department_id: Array.isArray(advancedFilters.department_id) && advancedFilters.department_id.length > 0
      ? advancedFilters.department_id.join(",")
      : (selectedDepartment === "all" ? "" : selectedDepartment),
    search: debouncedSearchTerm, // Use backend search for accurate results
    award_id: Array.isArray(advancedFilters.award_id) && advancedFilters.award_id.length > 0
      ? advancedFilters.award_id.join(",")
      : "",
    employee_id: login_user_role === "EMPLOYEE" && login_user?.id
      ? String(login_user.id)
      : (Array.isArray(advancedFilters.employee_id) && advancedFilters.employee_id.length > 0
        ? advancedFilters.employee_id.join(",")
        : ""),
  });

  // Fallback: Populate quarters from certificates data if options are empty (for Manager/Employee)
  useEffect(() => {
    if (quarterOptions.length <= 1 && rawCertificates) {
      const certificatesArray = Array.isArray(rawCertificates) ? rawCertificates : Object.values(rawCertificates);
      const extractedQuarters = new Map();

      certificatesArray.forEach(award => {
        if (award.periods && Array.isArray(award.periods)) {
          award.periods.forEach(p => {
            const pid = String(p.period_id || p.id);
            if (pid && !extractedQuarters.has(pid)) {
              extractedQuarters.set(pid, {
                label: p.period_label || p.label,
                value: pid,
                period_label: p.period_label || p.label
              });
            }
          });
        }
      });

      if (extractedQuarters.size > 0) {
        setQuarterOptions([
          { label: "All Quarters", value: "all" },
          ...Array.from(extractedQuarters.values()).sort((a, b) => a.label.localeCompare(b.label))
        ]);
      }
    }
  }, [rawCertificates, quarterOptions.length]);


  const [deleteAward] = useDeleteAwardMutation();
  const [downloadCertificateTrigger] = useLazyDownloadCertificateQuery();

  // Handle refresh and clear selection
  const handleRefresh = async () => {
    try {
      await refetch();
      setCheckedRows({});
    } catch (error) {
      console.error("Refresh failed:", error);
    }
  };

  // Fetch all departments
  const { data: department_list = [] } = useFetchGetallDepartmentsQuery();

  const handleYearChange = (newValue) => {
    setSelectedYear(newValue);
    setSearchTerm("");
  };

  const handleQuarterChange = (event) => {
    setQuarter(event.target.value);
    setSearchTerm("");
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
    setSearchTerm("");
  };



  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value === "") {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const handleOpenFilter = () => setShowFilter(true);
  const handleCloseFilter = () => setShowFilter(false);

  // const handleApplyFilter = (data) => {
  //   setSearchTerm("");
  //   setAdvancedFilters(data);
  //   // Sync header filters only if a single value is selected for simplicity
  //   if (data.department_id && data.department_id.length === 1) {
  //     setSelectedDepartment(data.department_id[0]);
  //   } else if (data.department_id && data.department_id.length === 0) {
  //     setSelectedDepartment("all");
  //   }

  //   if (data.period_id && data.period_id.length === 1) {
  //     setQuarter(data.period_id[0]);
  //   } else if (data.period_id && data.period_id.length === 0) {
  //     setQuarter("all");
  //   }

  //   setShowFilter(false);
  // };

  const handleApplyFilter = (data) => {
    setSearchTerm(""); // clear search
    setAdvancedFilters(data); // keep all selected values

    // Show all selected values in header (comma separated) instead of only first
    setSelectedDepartment(
      Array.isArray(data.department_id)
        ? (data.department_id.length > 0 ? data.department_id.join(",") : "all")
        : (data.department_id ? String(data.department_id) : "all")
    );

    setQuarter(
      Array.isArray(data.period_id)
        ? (data.period_id.length > 0 ? data.period_id.join(",") : "all")
        : (data.period_id ? String(data.period_id) : "all")
    );

    setShowFilter(false);
  };

  // Check if any filter is active
  const isFilterActive =
    (Array.isArray(advancedFilters.employee_id) ? advancedFilters.employee_id.length > 0 : (advancedFilters.employee_id !== "all" && advancedFilters.employee_id !== "")) ||
    (Array.isArray(advancedFilters.department_id) ? advancedFilters.department_id.length > 0 : advancedFilters.department_id !== "all") ||
    (Array.isArray(advancedFilters.period_id) ? advancedFilters.period_id.length > 0 : advancedFilters.period_id !== "all") ||
    (Array.isArray(advancedFilters.award_id) ? advancedFilters.award_id.length > 0 : advancedFilters.award_id !== "all");

  const handleClearAllFilters = () => {
    setLoading(true);
    const clearedValues = {
      employee_id: [],
      department_id: [],
      period_id: [],
      award_id: [],
    };
    setAdvancedFilters(clearedValues);
    setQuarter("all");
    setSelectedDepartment("all");
    setSearchTerm("");

    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const handleGenerateCertificate = () => {
    setSearchTerm("");
    setIsEdit(false);
    setEditData(null);
    setShowOffcanvas(true);
  };

  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false);
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    event.preventDefault();
    setMenuAnchorEl(event.currentTarget);
    setMenuAnchorPos({ top: event.clientY, left: event.clientX });
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuAnchorPos(null);
    setSelectedRow(null);
  };

  const [checkedRows, setCheckedRows] = useState({});
  const [isAllSelected, setIsAllSelected] = useState(false);

  // We define handles after searchFilteredCertificates or just rely on state
  const handleCheckboxChange = (rowId, allData) => {
    const newCheckedRows = { ...checkedRows, [rowId]: !checkedRows[rowId] };
    setCheckedRows(newCheckedRows);
    const allChecked = allData.length > 0 && allData.every((row) => newCheckedRows[row.id]);
    setIsAllSelected(allChecked);
  };

  const handleSelectAll = (e, allData) => {
    const checked = e.target.checked;
    setIsAllSelected(checked);
    const newCheckedRows = {};
    if (checked) {
      // Only select child rows
      allData.filter(r => r.isChild).forEach((row) => {
        newCheckedRows[row.id] = true;
      });
    }
    setCheckedRows(newCheckedRows);
  };

  const handleEdit = () => {
    if (selectedRow) {
      setSearchTerm("");
      setEditData(selectedRow);
      setIsEdit(true);
      setShowOffcanvas(true);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedRow) {
      handleMenuClose();

      const confirmDelete = await Swal.fire({
        title: `Confirm Deletion`,
        html: `Are you sure you want to delete this certificate record?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Delete it!",
        showCloseButton: true,
        cancelButtonText: "Cancel",
        customClass: {
          popup: "animate__animated animate__fadeInLeft animate__faster custom-swal-popup",
          title: "custom-swal-title",
          htmlContainer: "custom-swal-html",
          icon: "custom-swal-icon",
          confirmButton: "swal-confirm-btn",
          cancelButton: "swal-cancel-btn",
        },
        showClass: {
          popup: "animate__animated animate__fadeInLeft ",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutRight",
        },
      });

      if (confirmDelete.isConfirmed) {
        try {
          const response = await deleteAward(selectedRow).unwrap();

          Swal.fire({
            title: "Deleted!",
            text: response?.message || "Award deleted successfully.",
            icon: "success",
            confirmButtonText: "OK",
            customClass: {
              popup: "custom-swal-popup",
              confirmButton: "swal-confirm-btn",
              title: "custom-swal-title",
              text: "custom-swal-html",
              icon: "custom-swal-icon",
            },
          });

          setSearchTerm("");
          handleRefresh();
        } catch (error) {
          toast.error(error?.data?.message || error?.message || "Failed to delete award.");
        }
      }
    } else {
      handleMenuClose();
    }
  };

  const handleDownload = async (row) => {
    if (!row.certificate_id) {
      toast.error("Certificate ID not available for this record.");
      return;
    }
    try {
      const timestamp = new Date().getTime();
      const response = await downloadCertificateTrigger({
        certificate_id: row.certificate_id,
        t: timestamp
      }).unwrap();

      let url = response?.data?.certificate_url || response?.certificate_url;

      if (url) {
        // Append timestamp to bypass caching of the generated certificate image/pdf
        url = url.includes("?") ? `${url}&t=${timestamp}` : `${url}?t=${timestamp}`;

        if (/iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
          openInNewTab(`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`);
        } else {
          openInNewTab(url);
        }
      } else {
        toast.error("Certificate URL not found.");
      }
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Failed to download certificate.");
    }
  };

  const handlePreview = (row) => {
    // For preview, we can reuse the download logic or redirect to a specific preview page/modal
    // For now, we'll follow the same logic as download to show the certificate
    handleDownload(row);
  };

  const certificateColumns = [
    {
      field: "actions",
      headerName: "Action",
      width: login_user_role === "ADMIN" ? 150 : 100,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Box display="flex" alignItems="center" gap={1}>
          Action
        </Box>
      ),
      renderCell: (params) => {
        const row = params.row;
        if (row.isParent) {
          return (
            <Box display="flex" alignItems="center" gap={0} ml={0.5}>
              <IconButton
                onClick={() => toggleGroup(row.id)}
                sx={{ width: 42, height: 42 }}
              >
                {expandedGroups[row.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          );
        }
        if (row.isPeriod) {
          return (
            <Box display="flex" alignItems="center" gap={0} ml={4}>
              <IconButton
                onClick={() => toggleGroup(row.id)}
                sx={{ width: 42, height: 42 }}
              >
                {expandedGroups[row.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          );
        }
        return (
          <Box display="flex" alignItems="center" height="100%" gap={0} ml={5.5}>
            {login_user_role === "ADMIN" && (
              <>
                <Checkbox
                  checked={!!checkedRows[row.id]}
                  onChange={() => handleCheckboxChange(row.id, searchFilteredCertificates.filter(r => r.isChild))}
                  sx={{ width: 42, height: 42, '& .MuiSvgIcon-root': { fontSize: 20 } }}
                />
                <IconButton
                  disabled={!checkedRows[row.id]}
                  onClick={(event) => handleMenuOpen(event, row)}
                >
                  <MoreVertIcon />
                </IconButton>
              </>
            )}
          </Box>
        );
      },
    },


    {
      field: "award_name",
      headerName: "Name",
      width: 240,
      sortable: true,
      renderCell: (p) => {
        let content = null;
        if (p.row.isParent) {
          content = (
            <Box display="flex" flexDirection="column" gap={0.5}>
              <span style={{ fontWeight: 600, whiteSpace: 'normal', lineHeight: '1.2' }}>{p.value}</span>
            </Box>
          );
        } else if (p.row.isPeriod) {
          content = <span style={{ fontWeight: 600, whiteSpace: 'normal', lineHeight: '1.2' }}>{p.row.period_label}</span>;
        } else if (p.row.isChild) {
          content = <span style={{ fontSize: '14px' }}>{p.row.employee_name}</span>;
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            {content}
          </Box>
        );
      }
    },
    {
      field: "awarded_date",
      headerName: "Award Date",
      width: 200,
      sortable: true,
      renderCell: (p) => (
        <Box display="flex" alignItems="center" height="100%">
          {p.value || ""}
        </Box>
      )
    },
    {
      field: "added_at",
      headerName: "Added At",
      width: 180,
      sortable: true,
      renderCell: (p) => (
        <Box display="flex" alignItems="center" height="100%">
          {p.value || ""}
        </Box>
      )
    },
    {
      field: "added_by",
      headerName: "Added By",
      width: 150,
      sortable: true,
      renderCell: (p) => (
        <Box display="flex" alignItems="center" height="100%">
          {p.value || ""}
        </Box>
      )
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 180,
      sortable: true,
      renderCell: (p) => (
        <Box display="flex" alignItems="center" height="100%">
          {p.value || ""}
        </Box>
      )
    },
    {
      field: "updated_by",
      headerName: "Updated By",
      width: 150,
      sortable: true,
      renderCell: (p) => (
        <Box display="flex" alignItems="center" height="100%">
          {p.value || ""}
        </Box>
      )
    },
    {
      field: "download_action",
      headerName: "Action",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        if (!params.row.isChild) return null;
        return (
          <Box display="flex" alignItems="center" height="100%" gap={1}>
            <Button
              size="small"
              startIcon={<MdOutlineFileDownload size={18} />}
              onClick={() => handleDownload(params.row)}
              sx={{
                textTransform: 'none',
                backgroundColor: isDarkMode ? 'rgba(4, 161, 137, 0.15)' : '#e6f4f1',
                color: isDarkMode ? '#0dbfa5' : '#04a189',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(4, 161, 137, 0.25)' : '#d1e9e4',
                },
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 10px',
                borderRadius: '4px'
              }}
            >
              Download
            </Button>
            <Button
              size="small"
              startIcon={<MdOutlineVisibility size={18} />}
              onClick={() => handlePreview(params.row)}
              sx={{
                textTransform: 'none',
                backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.15)' : '#e3f2fd',
                color: isDarkMode ? '#42a5f5' : '#1976d2',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.25)' : '#bbdefb',
                },
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 10px',
                borderRadius: '4px'
              }}
            >
              Preview
            </Button>
          </Box>
        );
      }
    },
  ];

  // Build a department map for O(1) lookups
  const deptMap = {};
  department_list.forEach((dept) => {
    deptMap[dept.department_id] = dept.department_name;
  });

  // Convert to array in case the backend returns an object dictionary instead of a sequential array
  const formattedCertificates = Array.isArray(rawCertificates)
    ? rawCertificates
    : (rawCertificates ? Object.values(rawCertificates) : []);

  // Filter by selected quarter and department before flattening
  const flattenedCertificates = [...formattedCertificates].reverse().flatMap((award, awardIndex) => {
    const parentId = `award-${award?.award_id || awardIndex}`;
    const availablePeriods = award.periods || [];
    const resultRows = [];
    let hasMatchingEmployeesOverall = false;
    const periodData = [];

    const parentRow = {
      id: parentId,
      isParent: true,
      award_name: award?.award_name || "",
      employee_name: "",
      period_code: "",
      period_label: "",
      award_year: award?.award_year || "",
      added_at: "",
      added_by: "",
      updated_at: "",
      updated_by: "",
      quarter: "",
      achievement_summary: award?.description || "",
      department_name: "",
      award_id: award?.award_id,
      period_id: "",
      awarded_date: "",
      award_date_issued: award?.award_year ? `${award.award_year}-01-01` : "",
      all_employees: []
    };

    if (availablePeriods.length > 0) {
      availablePeriods.forEach((period) => {
        let matchingEmployees = [];
        if (period.employees && Array.isArray(period.employees)) {
          matchingEmployees = period.employees.filter((emp) => {
            // Security Filter: Production-level check to ensure Employees only see their own data
            const securityFilter = login_user_role === "EMPLOYEE"
              ? String(emp?.user_id) === String(login_user?.id)
              : true;

            if (!securityFilter) return false;

            // 1. Department Filter (Header or Advanced Multi-select)
            const deptFilter =
              advancedFilters.department_id && advancedFilters.department_id.length > 0
                ? advancedFilters.department_id.map(String).includes(String(emp?.user?.department_id))
                : selectedDepartment === "all" || String(emp?.user?.department_id) === String(selectedDepartment);

            // 2. Quarter Filter (Header or Advanced Multi-select)
            const periodFilter =
              advancedFilters.period_id && advancedFilters.period_id.length > 0
                ? advancedFilters.period_id.map(String).includes(String(period?.period_id))
                : quarter === "all" || String(period?.period_id) === String(quarter);

            // 3. Employee Filter (Advanced)
            const employeeFilter = Array.isArray(advancedFilters.employee_id)
              ? advancedFilters.employee_id.length === 0
                ? true
                : advancedFilters.employee_id.map(String).includes(String(emp?.user_id))
              : advancedFilters.employee_id === "all" || !advancedFilters.employee_id
                ? true
                : String(emp?.user_id) === String(advancedFilters.employee_id);

            // 4. Year Filter (Header)
            const yearFilter = !selectedYear || String(emp.award_year) === String(dayjs(selectedYear).year());

            // 5. Search Filter
            const searchLower = searchTerm.toLowerCase();
            const searchMatch =
              !searchTerm ||
              (emp?.user?.name || "").toLowerCase().includes(searchLower) ||
              (award?.award_name || "").toLowerCase().includes(searchLower) ||
              (period?.period_label || "").toLowerCase().includes(searchLower) ||
              (period?.period_code || "").toLowerCase().includes(searchLower) ||
              (deptMap[emp?.user?.department_id] || "").toLowerCase().includes(searchLower) ||
              (String(emp?.award_year) || "").toLowerCase().includes(searchLower);

            return deptFilter && periodFilter && employeeFilter && yearFilter && searchMatch;
          });
        }

        if (matchingEmployees.length > 0) {
          hasMatchingEmployeesOverall = true;
          // Collect period data to add later if needed
          periodData.push({ period, matchingEmployees });
        }
      });
    }

    if (hasMatchingEmployeesOverall) {
      resultRows.push(parentRow);
      const expandedAward = !!expandedGroups[parentId];

      if (expandedAward) {
        periodData.forEach(({ period, matchingEmployees }) => {
          const periodId = `${parentId}-period-${period.period_id}`;
          const expandedPeriod = !!expandedGroups[periodId];

          resultRows.push({
            id: periodId,
            isPeriod: true,
            parentId: parentId,
            award_name: award?.award_name || "",
            employee_name: "",
            period_code: period.period_code || "",
            period_label: period.period_label || "",
            award_year: award?.award_year || "",
            added_at: "",
            added_by: "",
            updated_at: "",
            updated_by: "",
            quarter: period.period_label || "",
            achievement_summary: "",
            department_name: "",
            award_id: award?.award_id,
            period_id: period.period_id || "",
            awarded_date: "",
            award_date_issued: "",
            all_employees: matchingEmployees.map((e) => e.user_id),
          });

          if (expandedPeriod) {
            matchingEmployees.forEach((emp, empIndex) => {
              resultRows.push({
                id: emp?.award_employee_id || `row-${periodId}-${empIndex}`,
                parentId: periodId,
                isChild: true,
                award_employee_id: emp?.award_employee_id,
                award_name: award?.award_name || "",
                employee_name: emp?.user?.name || "",
                period_code: period.period_code || "",
                period_label: period.period_label || "",
                award_year: emp?.award_year || award?.award_year || "",
                added_at: emp?.added_at ? dayjs(emp.added_at).format("DD-MM-YYYY") : "",
                added_by: emp?.added_by || award?.added_by || "",
                updated_at: emp?.updated_at ? dayjs(emp.updated_at).format("DD-MM-YYYY") : "",
                updated_by: emp?.updated_by || award?.updated_by || "",
                quarter: period.period_label || "",
                achievement_summary: award?.description || "",
                department_name: deptMap[emp?.user?.department_id] || "",
                award_id: award?.award_id,
                period_id: period.period_id || "",
                awarded_date: (() => {
                  const datePart = (emp?.awarded_date || emp?.added_at) ? dayjs(emp?.awarded_date || emp?.added_at).format("DD/MM/YYYY") : "";
                  const timePart = emp?.added_at ? dayjs(emp.added_at).format("hh:mm A") : "";
                  return datePart && timePart ? `${datePart} -- ${timePart}` : datePart;
                })(),
                award_date_issued: emp?.awarded_date || (emp?.award_year ? `${emp.award_year}-01-01` : ""),
                certificate_id: emp?.certificate_id || "",
                employee_id: emp?.user_id || "",
              });
            });
          }
        });
      }
      return resultRows;
    }
    return [];
  });

  const searchFilteredCertificates = flattenedCertificates;

  if (login_user_role === "CLIENT") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography variant="h6" color="textSecondary">You do not have permission to view this module.</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Offcanvas - Generate Certificate Form (Admin only) */}
      <Offcanvas
        show={showOffcanvas}
        placement="end"
        className="custom-offcanvas"
        style={{ width: window.innerWidth <= 768 ? "85%" : "50%", zIndex: 1300 }}
      >
        <Offcanvas.Body className="p-0">
          {showOffcanvas && (
            <AddCertificate
              onCancel={() => setShowOffcanvas(false)}
              fetchCertificates={handleRefresh}
              setSelectedYear={setSelectedYear}
              isEdit={isEdit}
              editData={editData}
            />
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        show={showFilter}
        onHide={handleCloseFilter}
        placement="end"
        className="custom-offcanvas"
        style={{ width: window.innerWidth <= 768 ? "85%" : "50%", zIndex: 1300 }}
      >
        <Offcanvas.Body className="p-0">
          <FilterCertificate
            onApply={handleApplyFilter}
            onCancel={handleCloseFilter}
            currentFilters={advancedFilters}
            login_user_role={login_user_role}
            rawCertificates={rawCertificates}
          />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Action Menu - Edit / Delete */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuAnchorPos}
        open={Boolean(menuAnchorPos)}
        onClose={handleMenuClose}
        disableAutoFocus={true}
        disableEnforceFocus={true}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon sx={{ minWidth: "30px", color: "#0d6efd" }}>
            <RiEditLine size={18} color="#0d6efd" />
          </ListItemIcon>
          <ListItemText>Edit Certificate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#ed2828ff" }}>
          <ListItemIcon sx={{ minWidth: "30px", color: "#ed2828ff" }}>
            <RiDeleteBin5Line size={18} color="#ed2828ff" />
          </ListItemIcon>
          <ListItemText>Delete Certificate</ListItemText>
        </MenuItem>
      </Menu>

      <div className={`container-fluid ${isDarkMode ? "project-drawer-bg text-white" : "bg-white"} p-3`}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: { xs: "wrap", md: "nowrap" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
            border: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
            borderRadius: "6px",
            padding: "12px 16px",
            marginBottom: "3px",
            backgroundColor: isDarkMode ? "transparent" : "#fff",
          }}
        >
          {/* Left Part */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
              flex: { sm: 2, md: "1 1 0" },
              minWidth: { sm: "max-content", md: "max-content" }
            }}
          >
            {/* Quarter */}
            <FormControl sx={{ minWidth: { xs: 150, md: 120, lg: 150 } }} size="small">
              <Select value={quarter} onChange={handleQuarterChange}>
                {quarterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Department - Hidden for Employees */}
            {login_user_role !== "EMPLOYEE" && (
              <FormControl sx={{ minWidth: { xs: 180, md: 140, lg: 180 } }} size="small">
                <Select
                  value={selectedDepartment}
                  onChange={handleDepartmentChange}
                  displayEmpty
                  sx={{ width: "100%" }}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {department_list.map((dept) => (
                    <MenuItem key={dept.department_id} value={dept.department_id}>
                      {dept.department_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Center Part */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "center", md: "center" },
              flex: { sm: 1, md: "1 1 0" },
              minWidth: { md: "max-content" },
              width: { sm: "100%", md: "auto" }
            }}
          >
            <DatePicker
              views={["year"]}
              value={selectedYear}
              onChange={handleYearChange}
              minDate={dayjs("2000-01-01")}
              maxDate={maxSelectableYear}
              slotProps={{
                popper: {
                  sx: {
                    ...(isDarkMode ? {
                      "& .MuiPaper-root": {
                        backgroundColor: "#1f1f1f",
                        color: "#ffffff",
                      },
                    } : {}),
                    "& .MuiPickersYear-yearButton": {
                      color: isDarkMode ? "#ffffff" : "inherit",
                      borderRadius: "18px",
                    },
                    "& .MuiPickersYear-yearButton:hover": {
                      backgroundColor: "rgba(18, 183, 159, 0.18)",
                    },
                    "& .MuiPickersYear-yearButton.Mui-selected": {
                      backgroundColor: "rgba(18, 183, 159, 1) !important",
                      color: "#ffffff !important",
                      fontWeight: 600,
                    },
                    "& .MuiPickersYear-yearButton.Mui-selected:hover": {
                      backgroundColor: "rgba(16, 160, 139, 1) !important",
                    },
                    "& .MuiPickersYear-yearButton.Mui-selected:focus": {
                      backgroundColor: "rgba(18, 183, 159, 1) !important",
                    },
                  },
                },
                textField: {
                  size: "small",
                  sx: {
                    width: { xs: "100%", sm: "100%", md: 125 },
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "rgba(18, 183, 159, 1)",
                      },
                    },
                  }
                }
              }}
            />
          </Box>

          {/* Right Part */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: { xs: "flex-start", sm: "flex-end", md: "flex-end" },
              gap: 2,
              flex: { sm: "1 1 100%", md: "1 1 0" },
              minWidth: { sm: "max-content" }
            }}
          >
            <Box sx={{ flex: { sm: 1, md: "initial" }, width: { xs: "100%", sm: "auto" } }}>
              <SearchBar
                placeholder="Search"
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                width: { xs: "100%", sm: "auto" }
              }}
            >
              {isFilterActive && (
                <Box sx={{ flex: { xs: 1, sm: "initial" }, display: "flex", width: { xs: "100%", sm: "auto" } }}>
                  <button
                    onClick={handleClearAllFilters}
                    className="addmore_btn text-nowrap"
                    style={{ padding: "0 21px", height: "40px", width: "100%" }}
                    disabled={isLoading}
                  >
                    Clear Filter
                  </button>
                </Box>
              )}

              {login_user_role === "ADMIN" && (
                <Box sx={{ flex: { xs: 1, sm: "initial" }, display: "flex", width: { xs: "100%", sm: "auto" } }}>
                  <button
                    className="rounded text-capitalize AddButton"
                    onClick={handleGenerateCertificate}
                    style={{ height: 40, whiteSpace: "nowrap", padding: "0 10px", borderRadius: "8px", border: "none", width: "100%", fontSize: "14px" }}
                  >
                    Generate Certificate
                  </button>
                </Box>
              )}

              <Tooltip title="Filter">
                <IconButton
                  sx={{
                    borderRadius: "50%",
                    width: "38px",
                    height: "38px",
                    flexShrink: 0,
                    backgroundColor: isDarkMode
                      ? "rgba(67, 105, 255, 0.08)"
                      : "#F1F5FF",
                    "&:hover": {
                      backgroundColor: "var(--icon-hover-bg)",
                    },
                  }}
                  onClick={handleOpenFilter}
                >
                  <CiFilter className="text-rgbr-blue" style={{ fontSize: "23px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Certificate Table */}
        {(isLoading || isFetching || loading) ? (
          <Loader />
        ) : (
          <div className="row">
            <div className="col-12 mt-3">
              <Box sx={{ width: "100%" }}>
                <DataGrid
                  rows={searchFilteredCertificates}
                  columns={certificateColumns}
                  loading={isLoading || isFetching || loading}
                  disableColumnMenu  //added  disableColumnMenu for hide 3 dots for each header cells
                  slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                  getRowId={(row) => row.id || Math.random()}
                  pagination
                  pageSizeOptions={[10, 20, 30, 40]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  paginationMode="client"
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 60}
                  sx={{
                    border: `1px solid ${isDarkMode ? "#333" : "#e0e0e0"}`,
                    maxWidth: "100%",
                    overflowX: "auto",
                    "& .MuiDataGrid-cell": {
                      borderBottom: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                      py: 1
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      borderBottom: isDarkMode ? "2px solid #333" : "2px solid #e0e0e0",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: isDarkMode ? "#1d1d1d" : "#fafafa",
                    },
                    backgroundColor: isDarkMode ? "project-drawer-bg" : "inherit",
                    "& .MuiDataGrid-columnHeaderTitle": {
                      color: isDarkMode ? "#fff" : "inherit",
                      fontWeight: 400
                    },
                    "& .MuiDataGrid-footerContainer": {
                      backgroundColor: isDarkMode ? "project-drawer-bg" : "inherit",
                      color: isDarkMode ? "#fff" : "inherit",
                      borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                    },
                    "& .MuiTablePagination-root": {
                      color: isDarkMode ? "#fff" : "inherit",
                    }
                  }}
                />
              </Box>
            </div>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default Certificate;
